import { useState, useCallback } from 'react';
import { EditorInstance } from 'novel';

interface UseChangeIndicatorsOptions {
    editorInstance: EditorInstance | null;
    showChangeIndicators?: boolean;
    isAgentMode?: boolean;
    isAiProcessing?: boolean;
}

interface UseChangeIndicatorsReturn {
    hasActiveIndicators: boolean;
    activateIndicators: () => void;
    clearIndicators: () => void;
    handleEditorUpdate: (transaction: any) => void;
}

/**
 * Hook to manage change indicator state within EditorCore.
 * 
 * - `activateIndicators`: Called after AI content updates to show change decorations
 * - `clearIndicators`: Called to hide decorations (e.g., when user starts editing)
 * - `handleEditorUpdate`: Called on each editor transaction to auto-clear on manual edits
 */
export function useChangeIndicators({
    editorInstance,
    showChangeIndicators = true,
    isAgentMode = false,
    isAiProcessing = false,
}: UseChangeIndicatorsOptions): UseChangeIndicatorsReturn {
    const [hasActiveIndicators, setHasActiveIndicators] = useState(false);

    // Clear change indicator decorations
    const clearIndicators = useCallback(() => {
        if (!editorInstance || !showChangeIndicators || !isAgentMode) {
            setHasActiveIndicators(false);
            return;
        }

        // Disable the extension to stop expensive apply() method on every keystroke
        const changeIndicatorExt = editorInstance.extensionManager.extensions.find(
            ext => ext.name === 'changeIndicator'
        );
        if (changeIndicatorExt) {
            changeIndicatorExt.options.enabled = false;
        }

        // Trigger update to clear decorations
        const tr = editorInstance.state.tr;
        tr.setMeta('updateChangeIndicators', true);
        editorInstance.view.dispatch(tr);

        setHasActiveIndicators(false);
    }, [editorInstance, showChangeIndicators, isAgentMode]);

    // Activate indicators (called after AI content updates)
    const activateIndicators = useCallback(() => {
        setHasActiveIndicators(true);
    }, []);

    // Handle editor update - auto-clear on manual (non-AI) document changes
    const handleEditorUpdate = useCallback((transaction: any) => {
        if (
            hasActiveIndicators &&
            !isAiProcessing &&
            !transaction.getMeta('updateChangeIndicators') &&
            transaction.docChanged
        ) {
            clearIndicators();
        }
    }, [hasActiveIndicators, isAiProcessing, clearIndicators]);

    return {
        hasActiveIndicators,
        activateIndicators,
        clearIndicators,
        handleEditorUpdate,
    };
}
